import { Controller } from "@hotwired/stimulus"
import paper from "paper"

/**
 * Flow Canvas Controller - n8n-style visual flow editor
 * 
 * Features:
 * - Full flow graph rendering with triggers, actions, and chains
 * - Pan & zoom navigation
 * - Drag-and-drop node positioning
 * - Click-to-edit via modals
 * - Wire/cable connections with grab handles
 * - Auto-layout for initial positioning
 */
export default class extends Controller {
  static targets = ["canvas", "loading", "zoomLevel", "modal", "modalContent", "nodeModalTemplate", "chainModalTemplate"]
  
  static values = {
    actionId: Number,
    flowUrl: String,
    updateUrl: String,
    availableActions: Array,
    gridSize: { type: Number, default: 20 },
    nodeWidth: { type: Number, default: 220 },
    nodeHeight: { type: Number, default: 100 }
  }

  connect() {
    this.setupPaper()
    this.loadFlow()
  }

  disconnect() {
    if (this.paperScope) {
      this.paperScope.remove()
    }
  }

  // === Setup ===

  setupPaper() {
    this.paperScope = new paper.PaperScope()
    this.paperScope.setup(this.canvasTarget)
    
    this.project = this.paperScope.project
    this.view = this.paperScope.view
    
    // Layers for z-ordering
    this.gridLayer = new this.paperScope.Layer({ name: 'grid' })
    this.cablesLayer = new this.paperScope.Layer({ name: 'cables' })
    this.nodesLayer = new this.paperScope.Layer({ name: 'nodes' })
    
    // State
    this.nodes = new Map()
    this.cables = new Map()
    this.graphData = null
    this.zoom = 1
    this.isPanning = false
    this.panStart = null
    this.selectedNode = null
    this.dragOffset = null
    this.hoveredCable = null
    this.isDirty = false
    
    // Colors
    this.colors = {
      trigger: { bg: '#059669', accent: '#10b981', icon: '#ffffff' },
      action: { bg: '#1f2937', accent: '#374151', icon: '#ffffff' },
      outlet: { bg: '#ffffff', accent: '#e5e7eb', icon: '#1f2937' },
      cable: { normal: '#10b981', hover: '#059669', disabled: '#9ca3af' },
      connector: { normal: '#10b981', hover: '#059669' },
      grabHandle: { normal: '#f59e0b', hover: '#d97706' }
    }
    
    // Action type colors
    this.actionTypeColors = {
      slack_message: '#4A154B',
      http_forward: '#2563eb',
      log: '#d97706',
      email: '#16a34a',
      agent: '#7c3aed'
    }
    
    this.setupEventHandlers()
    this.drawGrid()
  }

  drawGrid() {
    this.gridLayer.activate()
    this.gridLayer.removeChildren()
    
    const bounds = this.view.bounds
    const gridSize = this.gridSizeValue
    
    // Extended grid for panning
    const padding = 2000
    
    for (let x = bounds.x - padding; x < bounds.x + bounds.width + padding; x += gridSize) {
      for (let y = bounds.y - padding; y < bounds.y + bounds.height + padding; y += gridSize) {
        new this.paperScope.Path.Circle({
          center: [x, y],
          radius: 1,
          fillColor: '#e5e7eb'
        })
      }
    }
    
    this.nodesLayer.activate()
  }

  // === Data Loading ===

  async loadFlow() {
    try {
      const response = await fetch(this.flowUrlValue)
      const data = await response.json()
      
      this.graphData = data.graph
      this.availableActionsValue = data.available_actions
      
      this.renderFlow()
      this.hideLoading()
    } catch (error) {
      console.error('Failed to load flow:', error)
      this.hideLoading()
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('hidden')
    }
  }

  // === Rendering ===

  renderFlow() {
    this.nodesLayer.activate()
    this.nodesLayer.removeChildren()
    this.nodes.clear()
    
    this.cablesLayer.activate()
    this.cablesLayer.removeChildren()
    this.cables.clear()
    
    if (!this.graphData) return
    
    const { nodes, edges } = this.graphData
    
    // Auto-layout if nodes don't have positions
    const layoutedNodes = this.autoLayout(nodes, edges)
    
    // Render nodes
    layoutedNodes.forEach(node => {
      if (node.type === 'trigger') {
        this.renderTriggerNode(node)
      } else if (node.type === 'action') {
        this.renderActionNode(node)
      }
    })
    
    // Render edges (cables)
    edges.forEach(edge => {
      this.renderCable(edge)
    })
    
    this.nodesLayer.activate()
  }

  autoLayout(nodes, edges) {
    // Simple layered layout algorithm
    // Group nodes by their depth in the graph
    
    const nodeMap = new Map(nodes.map(n => [n.id, { ...n }]))
    const depths = new Map()
    const processed = new Set()
    
    // Find root nodes (triggers or nodes with no incoming edges)
    const hasIncoming = new Set(edges.map(e => e.target_id))
    const roots = nodes.filter(n => 
      n.type === 'trigger' || !hasIncoming.has(n.id)
    )
    
    // BFS to assign depths
    const queue = roots.map(n => ({ node: n, depth: 0 }))
    while (queue.length > 0) {
      const { node, depth } = queue.shift()
      if (processed.has(node.id)) continue
      processed.add(node.id)
      
      depths.set(node.id, depth)
      
      // Find outgoing edges
      const outgoing = edges.filter(e => {
        if (e.source_type === 'trigger' && e.source_id === node.id) return true
        if (e.source_type === 'action' && e.source_id === node.id) return true
        if (e.source_type === 'outlet') {
          // Check if outlet belongs to this action
          const actionNode = nodes.find(n => n.type === 'action' && n.id === node.id)
          if (actionNode?.outlets?.some(o => o.id === e.source_id)) return true
        }
        return false
      })
      
      outgoing.forEach(edge => {
        const targetNode = nodes.find(n => n.id === edge.target_id && n.type === edge.target_type)
        if (targetNode && !processed.has(targetNode.id)) {
          queue.push({ node: targetNode, depth: depth + 1 })
        }
      })
    }
    
    // Assign positions based on depth
    const depthGroups = new Map()
    nodes.forEach(node => {
      const depth = depths.get(node.id) ?? 0
      if (!depthGroups.has(depth)) depthGroups.set(depth, [])
      depthGroups.get(depth).push(node)
    })
    
    const startX = 100
    const startY = 100
    const xGap = 300
    const yGap = 150
    
    depthGroups.forEach((groupNodes, depth) => {
      groupNodes.forEach((node, index) => {
        const existingData = node.canvas_data || {}
        
        // Use existing position or calculate new one
        if (existingData.x == null || existingData.y == null) {
          nodeMap.get(node.id).canvas_data = {
            ...existingData,
            x: startX + (depth * xGap),
            y: startY + (index * yGap)
          }
        }
      })
    })
    
    return Array.from(nodeMap.values())
  }

  renderTriggerNode(node) {
    const position = new this.paperScope.Point(
      node.canvas_data?.x ?? 100,
      node.canvas_data?.y ?? 100
    )
    
    const group = new this.paperScope.Group()
    group.data = { type: 'trigger', node }
    
    // Card background
    const card = new this.paperScope.Path.Rectangle({
      point: position,
      size: [this.nodeWidthValue, 80],
      radius: 8,
      fillColor: this.colors.trigger.bg,
      shadowColor: new this.paperScope.Color(0, 0, 0, 0.15),
      shadowBlur: 10,
      shadowOffset: new this.paperScope.Point(0, 4)
    })
    group.addChild(card)
    
    // Icon
    const iconBg = new this.paperScope.Path.Circle({
      center: [position.x + 30, position.y + 40],
      radius: 16,
      fillColor: this.colors.trigger.accent
    })
    group.addChild(iconBg)
    
    const icon = new this.paperScope.PointText({
      point: [position.x + 24, position.y + 46],
      content: this.getTriggerIcon(node.trigger_type),
      fontSize: 16
    })
    group.addChild(icon)
    
    // Title
    const title = new this.paperScope.PointText({
      point: [position.x + 55, position.y + 35],
      content: this.truncateText(node.name, 18),
      fontSize: 13,
      fontWeight: 'bold',
      fillColor: 'white'
    })
    group.addChild(title)
    
    // Type label
    const typeLabel = new this.paperScope.PointText({
      point: [position.x + 55, position.y + 52],
      content: this.getTriggerTypeLabel(node.trigger_type),
      fontSize: 10,
      fillColor: 'rgba(255,255,255,0.7)'
    })
    group.addChild(typeLabel)
    
    // Output connector
    const connector = new this.paperScope.Path.Circle({
      center: [position.x + this.nodeWidthValue, position.y + 40],
      radius: 8,
      fillColor: this.colors.connector.normal,
      strokeColor: 'white',
      strokeWidth: 2
    })
    connector.data = { type: 'output', nodeType: 'trigger', nodeId: node.id }
    group.addChild(connector)
    
    group.data.bounds = card.bounds.clone()
    group.data.outputConnector = connector
    
    this.nodes.set(`trigger-${node.id}`, group)
  }

  renderActionNode(node) {
    const position = new this.paperScope.Point(
      node.canvas_data?.x ?? 100,
      node.canvas_data?.y ?? 100
    )
    
    const typeColor = this.actionTypeColors[node.action_type] || this.colors.action.bg
    const hasOutlets = node.outlets && node.outlets.length > 0
    const nodeHeight = hasOutlets ? this.nodeHeightValue + (node.outlets.length * 40) : this.nodeHeightValue
    
    const group = new this.paperScope.Group()
    group.data = { type: 'action', node }
    
    // Card background
    const card = new this.paperScope.Path.Rectangle({
      point: position,
      size: [this.nodeWidthValue, nodeHeight],
      radius: 8,
      fillColor: this.colors.action.bg,
      shadowColor: new this.paperScope.Color(0, 0, 0, 0.15),
      shadowBlur: 10,
      shadowOffset: new this.paperScope.Point(0, 4)
    })
    group.addChild(card)
    
    // Type color accent bar
    const accentBar = new this.paperScope.Path.Rectangle({
      point: position,
      size: [4, nodeHeight],
      radius: [[8, 0], [0, 0], [0, 0], [8, 0]],
      fillColor: typeColor
    })
    group.addChild(accentBar)
    
    // Input connector
    const inputConnector = new this.paperScope.Path.Circle({
      center: [position.x, position.y + 50],
      radius: 8,
      fillColor: this.colors.connector.normal,
      strokeColor: 'white',
      strokeWidth: 2
    })
    inputConnector.data = { type: 'input', nodeType: 'action', nodeId: node.id }
    group.addChild(inputConnector)
    
    // Icon
    const iconBg = new this.paperScope.Path.Rectangle({
      point: [position.x + 12, position.y + 12],
      size: [36, 36],
      radius: 6,
      fillColor: typeColor
    })
    group.addChild(iconBg)
    
    const icon = new this.paperScope.PointText({
      point: [position.x + 22, position.y + 36],
      content: this.getActionIcon(node.action_type),
      fontSize: 16
    })
    group.addChild(icon)
    
    // Title
    const title = new this.paperScope.PointText({
      point: [position.x + 58, position.y + 28],
      content: this.truncateText(node.name, 16),
      fontSize: 13,
      fontWeight: 'bold',
      fillColor: 'white'
    })
    group.addChild(title)
    
    // Type label
    const typeLabel = new this.paperScope.PointText({
      point: [position.x + 58, position.y + 44],
      content: this.getActionTypeLabel(node.action_type),
      fontSize: 10,
      fillColor: 'rgba(255,255,255,0.7)'
    })
    group.addChild(typeLabel)
    
    // Output connector (for direct chains)
    const outputConnector = new this.paperScope.Path.Circle({
      center: [position.x + this.nodeWidthValue, position.y + 50],
      radius: 8,
      fillColor: this.colors.connector.normal,
      strokeColor: 'white',
      strokeWidth: 2
    })
    outputConnector.data = { type: 'output', nodeType: 'action', nodeId: node.id }
    group.addChild(outputConnector)
    
    // Render outlets
    const outletConnectors = []
    if (hasOutlets) {
      node.outlets.forEach((outlet, index) => {
        const outletY = position.y + 70 + (index * 40)
        
        // Outlet background
        const outletBg = new this.paperScope.Path.Rectangle({
          point: [position.x + 12, outletY],
          size: [this.nodeWidthValue - 24, 32],
          radius: 4,
          fillColor: 'rgba(255,255,255,0.1)'
        })
        group.addChild(outletBg)
        
        // Outlet icon
        const outletIcon = new this.paperScope.PointText({
          point: [position.x + 22, outletY + 20],
          content: this.getOutletIcon(outlet.outlet_type),
          fontSize: 12
        })
        group.addChild(outletIcon)
        
        // Outlet label
        const outletLabel = new this.paperScope.PointText({
          point: [position.x + 40, outletY + 20],
          content: this.truncateText(outlet.label, 14),
          fontSize: 11,
          fillColor: 'rgba(255,255,255,0.9)'
        })
        group.addChild(outletLabel)
        
        // Outlet output connector
        if (outlet.has_chain) {
          const outletConnector = new this.paperScope.Path.Circle({
            center: [position.x + this.nodeWidthValue, outletY + 16],
            radius: 6,
            fillColor: this.colors.grabHandle.normal,
            strokeColor: 'white',
            strokeWidth: 2
          })
          outletConnector.data = { type: 'output', nodeType: 'outlet', nodeId: outlet.id, actionId: node.id }
          group.addChild(outletConnector)
          outletConnectors.push({ outlet, connector: outletConnector })
        }
      })
    }
    
    group.data.bounds = card.bounds.clone()
    group.data.inputConnector = inputConnector
    group.data.outputConnector = outputConnector
    group.data.outletConnectors = outletConnectors
    
    this.nodes.set(`action-${node.id}`, group)
  }

  renderCable(edge) {
    const sourceKey = edge.source_type === 'trigger' 
      ? `trigger-${edge.source_id}`
      : edge.source_type === 'outlet'
        ? null  // Handled specially
        : `action-${edge.source_id}`
    
    const targetKey = `action-${edge.target_id}`
    
    let sourceConnector, targetConnector
    
    if (edge.source_type === 'outlet') {
      // Find the outlet connector from the action node
      this.nodes.forEach((group, key) => {
        if (group.data.type === 'action') {
          const outletConn = group.data.outletConnectors?.find(oc => oc.outlet.id === edge.source_id)
          if (outletConn) {
            sourceConnector = outletConn.connector
          }
        }
      })
    } else if (sourceKey) {
      const sourceNode = this.nodes.get(sourceKey)
      sourceConnector = sourceNode?.data.outputConnector
    }
    
    const targetNode = this.nodes.get(targetKey)
    targetConnector = targetNode?.data.inputConnector
    
    if (!sourceConnector || !targetConnector) return
    
    this.cablesLayer.activate()
    
    const from = sourceConnector.position
    const to = targetConnector.position
    
    // Create bezier cable
    const dx = to.x - from.x
    const controlOffset = Math.min(Math.abs(dx) * 0.5, 100)
    
    const path = new this.paperScope.Path({
      strokeColor: edge.enabled !== false ? this.colors.cable.normal : this.colors.cable.disabled,
      strokeWidth: 3,
      strokeCap: 'round'
    })
    
    path.add(from)
    path.cubicCurveTo(
      new this.paperScope.Point(from.x + controlOffset, from.y),
      new this.paperScope.Point(to.x - controlOffset, to.y),
      to
    )
    
    path.data = { type: 'cable', edge }
    
    // Grab handle in the middle
    const midPoint = path.getPointAt(path.length / 2)
    const grabHandle = new this.paperScope.Path.Circle({
      center: midPoint,
      radius: 8,
      fillColor: this.colors.grabHandle.normal,
      strokeColor: 'white',
      strokeWidth: 2
    })
    grabHandle.data = { type: 'grabHandle', edge, path }
    
    this.cables.set(`edge-${edge.id}`, { path, grabHandle, edge })
    
    this.nodesLayer.activate()
  }

  // === Event Handlers ===

  setupEventHandlers() {
    const tool = new this.paperScope.Tool()
    
    tool.onMouseDown = (event) => {
      const hitResult = this.project.hitTest(event.point, {
        fill: true,
        stroke: true,
        tolerance: 8
      })
      
      if (!hitResult) {
        // Start panning
        this.isPanning = true
        this.panStart = event.point
        this.canvasTarget.style.cursor = 'grabbing'
        return
      }
      
      const item = this.findParentGroup(hitResult.item)
      
      // Handle grab handle click
      if (hitResult.item?.data?.type === 'grabHandle') {
        this.openEdgeEditor(hitResult.item.data.edge)
        return
      }
      
      // Handle connector click (for creating new chains)
      if (hitResult.item?.data?.type === 'output') {
        // TODO: Start dragging a new cable
        return
      }
      
      // Handle node click
      if (item?.data?.type === 'trigger' || item?.data?.type === 'action') {
        this.selectedNode = item
        this.dragOffset = event.point.subtract(item.bounds.topLeft)
        item.bringToFront()
        this.canvasTarget.style.cursor = 'grabbing'
      }
    }
    
    tool.onMouseDrag = (event) => {
      if (this.isPanning) {
        const delta = event.point.subtract(this.panStart)
        this.view.center = this.view.center.subtract(delta)
        this.panStart = event.point
        return
      }
      
      if (this.selectedNode) {
        const newPos = event.point.subtract(this.dragOffset)
        const snappedPos = this.snapToGrid(newPos)
        const delta = snappedPos.subtract(this.selectedNode.bounds.topLeft)
        
        this.selectedNode.translate(delta)
        this.updateCablesForNode(this.selectedNode)
        this.isDirty = true
      }
    }
    
    tool.onMouseUp = () => {
      this.isPanning = false
      this.selectedNode = null
      this.dragOffset = null
      this.canvasTarget.style.cursor = 'grab'
    }
    
    tool.onMouseMove = (event) => {
      const hitResult = this.project.hitTest(event.point, {
        fill: true,
        stroke: true,
        tolerance: 8
      })
      
      // Reset cursor
      if (!hitResult) {
        this.canvasTarget.style.cursor = 'grab'
        this.clearHover()
        return
      }
      
      if (hitResult.item?.data?.type === 'grabHandle') {
        this.canvasTarget.style.cursor = 'pointer'
        this.highlightCable(hitResult.item.data)
      } else if (hitResult.item?.data?.type === 'output' || hitResult.item?.data?.type === 'input') {
        this.canvasTarget.style.cursor = 'crosshair'
        this.clearHover()
      } else {
        const item = this.findParentGroup(hitResult.item)
        if (item?.data?.type === 'trigger' || item?.data?.type === 'action') {
          this.canvasTarget.style.cursor = 'grab'
        }
        this.clearHover()
      }
    }
    
    // Double-click to edit
    this.canvasTarget.addEventListener('dblclick', (e) => {
      const rect = this.canvasTarget.getBoundingClientRect()
      const point = new this.paperScope.Point(
        (e.clientX - rect.left) / this.zoom,
        (e.clientY - rect.top) / this.zoom
      )
      
      const hitResult = this.project.hitTest(point, {
        fill: true,
        tolerance: 5
      })
      
      if (hitResult) {
        const item = this.findParentGroup(hitResult.item)
        if (item?.data?.type === 'action') {
          this.openActionEditor(item.data.node)
        } else if (item?.data?.type === 'trigger') {
          this.openTriggerEditor(item.data.node)
        }
      }
    })
  }

  updateCablesForNode(nodeGroup) {
    this.cables.forEach(({ path, grabHandle, edge }) => {
      // Check if this cable connects to/from the moved node
      const nodeData = nodeGroup.data
      
      let needsUpdate = false
      let sourceConnector = null
      let targetConnector = null
      
      if (nodeData.type === 'trigger' && edge.source_type === 'trigger' && edge.source_id === nodeData.node.id) {
        needsUpdate = true
      } else if (nodeData.type === 'action') {
        if (edge.source_type === 'action' && edge.source_id === nodeData.node.id) {
          needsUpdate = true
        }
        if (edge.target_type === 'action' && edge.target_id === nodeData.node.id) {
          needsUpdate = true
        }
        if (edge.source_type === 'outlet' && nodeData.node.outlets?.some(o => o.id === edge.source_id)) {
          needsUpdate = true
        }
      }
      
      if (needsUpdate) {
        // Remove old cable
        path.remove()
        grabHandle.remove()
        
        // Re-render
        this.cablesLayer.activate()
        this.renderCable(edge)
        this.nodesLayer.activate()
      }
    })
  }

  highlightCable({ path, grabHandle }) {
    if (this.hoveredCable) {
      this.clearHover()
    }
    
    path.strokeColor = this.colors.cable.hover
    path.strokeWidth = 4
    grabHandle.fillColor = this.colors.grabHandle.hover
    grabHandle.scaling = 1.2
    
    this.hoveredCable = { path, grabHandle }
  }

  clearHover() {
    if (this.hoveredCable) {
      this.hoveredCable.path.strokeColor = this.colors.cable.normal
      this.hoveredCable.path.strokeWidth = 3
      this.hoveredCable.grabHandle.fillColor = this.colors.grabHandle.normal
      this.hoveredCable.grabHandle.scaling = 1
      this.hoveredCable = null
    }
  }

  // === Actions ===

  zoomIn() {
    this.setZoom(this.zoom * 1.2)
  }

  zoomOut() {
    this.setZoom(this.zoom / 1.2)
  }

  resetZoom() {
    this.setZoom(1)
    this.view.center = new this.paperScope.Point(
      this.view.viewSize.width / 2,
      this.view.viewSize.height / 2
    )
  }

  setZoom(newZoom) {
    newZoom = Math.max(0.25, Math.min(2, newZoom))
    this.view.zoom = newZoom
    this.zoom = newZoom
    
    if (this.hasZoomLevelTarget) {
      this.zoomLevelTarget.textContent = `${Math.round(newZoom * 100)}%`
    }
  }

  async save() {
    if (!this.isDirty) return
    
    // Collect node positions
    const nodes = []
    this.nodes.forEach((group, key) => {
      const { type, node } = group.data
      nodes.push({
        type,
        id: node.id,
        canvas_data: {
          x: group.bounds.x,
          y: group.bounds.y
        }
      })
    })
    
    // Collect edge data (in case any were modified)
    const edges = []
    this.cables.forEach(({ edge }) => {
      edges.push(edge)
    })
    
    try {
      const response = await fetch(this.updateUrlValue, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ nodes, edges })
      })
      
      if (response.ok) {
        this.isDirty = false
        // Show success feedback
      }
    } catch (error) {
      console.error('Failed to save flow:', error)
    }
  }

  addAction() {
    // TODO: Open modal to create new action
    console.log('Add action')
  }

  openActionEditor(node) {
    if (!this.hasNodeModalTemplateTarget || !this.hasModalTarget) {
      window.location.href = `/actions/${node.id}/edit`
      return
    }
    
    const template = this.nodeModalTemplateTarget.content.cloneNode(true)
    const container = template.querySelector('div') || template
    
    // Populate node data
    const icon = container.querySelector('.node-icon')
    icon.textContent = this.getActionIcon(node.action_type)
    icon.className = `node-icon w-10 h-10 flex items-center justify-center text-lg ${this.getActionBgClass(node.action_type)}`
    
    container.querySelector('.node-name').textContent = node.name
    container.querySelector('.node-type').textContent = this.getActionTypeLabel(node.action_type)
    
    // Stats
    container.querySelector('.node-executions').textContent = node.execution_count || 0
    const successRate = node.execution_count > 0 
      ? Math.round((node.success_count / node.execution_count) * 100) + '%'
      : '—'
    container.querySelector('.node-success-rate').textContent = successRate
    
    // Status badge
    const statusBadge = container.querySelector('.node-status-badge')
    if (node.active) {
      statusBadge.textContent = 'Active'
      statusBadge.className = 'node-status-badge text-sm text-emerald-700 border-b-2 border-emerald-600 pb-0.5'
    } else {
      statusBadge.textContent = 'Inactive'
      statusBadge.className = 'node-status-badge text-sm text-gray-500'
    }
    
    // Chains info
    if (node.outlets && node.outlets.length > 0) {
      const chainsSection = container.querySelector('.node-chains')
      chainsSection.classList.remove('hidden')
      const chainsList = container.querySelector('.node-chains-list')
      chainsList.innerHTML = node.outlets.map(outlet => `
        <div class="flex items-center justify-between p-2 bg-gray-50 border border-gray-200 text-sm">
          <span>${this.getOutletIcon(outlet.outlet_type)} ${outlet.label}</span>
          ${outlet.has_chain ? '<span class="text-xs text-emerald-600">Connected</span>' : '<span class="text-xs text-gray-400">No chain</span>'}
        </div>
      `).join('')
    }
    
    // Links
    container.querySelector('.node-edit-link').href = `/actions/${node.id}/edit`
    container.querySelector('.node-view-link').href = `/actions/${node.id}`
    
    this.showModal(template)
  }

  openTriggerEditor(node) {
    if (!this.hasNodeModalTemplateTarget || !this.hasModalTarget) {
      return
    }
    
    const template = this.nodeModalTemplateTarget.content.cloneNode(true)
    const container = template.querySelector('div') || template
    
    // Populate trigger data
    const icon = container.querySelector('.node-icon')
    icon.textContent = this.getTriggerIcon(node.trigger_type)
    icon.className = 'node-icon w-10 h-10 flex items-center justify-center text-lg bg-emerald-100'
    
    container.querySelector('.node-name').textContent = node.name
    container.querySelector('.node-type').textContent = this.getTriggerTypeLabel(node.trigger_type)
    
    // Stats
    container.querySelector('.node-executions').textContent = node.execution_count || 0
    const successRate = node.execution_count > 0 
      ? Math.round((node.success_count / node.execution_count) * 100) + '%'
      : '—'
    container.querySelector('.node-success-rate').textContent = successRate
    
    // Status badge
    const statusBadge = container.querySelector('.node-status-badge')
    if (node.active) {
      statusBadge.textContent = 'Active'
      statusBadge.className = 'node-status-badge text-sm text-emerald-700 border-b-2 border-emerald-600 pb-0.5'
    } else {
      statusBadge.textContent = 'Inactive'
      statusBadge.className = 'node-status-badge text-sm text-gray-500'
    }
    
    // Hide chains section for triggers
    container.querySelector('.node-chains').classList.add('hidden')
    
    // Links - triggers are nested under their triggerable (webhook or schedule)
    const editLink = container.querySelector('.node-edit-link')
    const viewLink = container.querySelector('.node-view-link')
    
    if (node.triggerable_type === 'WebhookEndpoint') {
      editLink.href = `/inputs/${node.triggerable_id}/triggers/${node.id}/edit`
      viewLink.href = `/inputs/${node.triggerable_id}/triggers/${node.id}`
    } else if (node.triggerable_type === 'Schedule') {
      editLink.href = `/schedules/${node.triggerable_id}/triggers/${node.id}/edit`
      viewLink.href = `/schedules/${node.triggerable_id}/triggers/${node.id}`
    } else {
      editLink.classList.add('hidden')
      viewLink.classList.add('hidden')
    }
    
    this.showModal(template)
  }

  openEdgeEditor(edge) {
    if (!this.hasChainModalTemplateTarget || !this.hasModalTarget) {
      console.log('Edit edge:', edge)
      return
    }
    
    this.currentEdge = edge
    
    const template = this.chainModalTemplateTarget.content.cloneNode(true)
    const container = template.querySelector('div') || template
    
    // Description
    const description = container.querySelector('.chain-description')
    description.textContent = `${edge.source_type} → ${edge.target_type}`
    
    // Source info
    const sourceNode = this.graphData.nodes.find(n => 
      (edge.source_type === 'trigger' && n.type === 'trigger' && n.id === edge.source_id) ||
      (edge.source_type === 'action' && n.type === 'action' && n.id === edge.source_id) ||
      (edge.source_type === 'outlet' && n.type === 'action' && n.outlets?.some(o => o.id === edge.source_id))
    )
    
    const targetNode = this.graphData.nodes.find(n => 
      n.type === edge.target_type && n.id === edge.target_id
    )
    
    if (edge.source_type === 'outlet' && sourceNode) {
      const outlet = sourceNode.outlets.find(o => o.id === edge.source_id)
      container.querySelector('.chain-source-icon').textContent = this.getOutletIcon(outlet?.outlet_type)
      container.querySelector('.chain-source-name').textContent = outlet?.label || 'Outlet'
      container.querySelector('.chain-type-info').classList.remove('hidden')
    } else if (edge.source_type === 'trigger') {
      container.querySelector('.chain-source-icon').textContent = this.getTriggerIcon(sourceNode?.trigger_type)
      container.querySelector('.chain-source-name').textContent = sourceNode?.name || 'Trigger'
    } else {
      container.querySelector('.chain-source-icon').textContent = this.getActionIcon(sourceNode?.action_type)
      container.querySelector('.chain-source-name').textContent = sourceNode?.name || 'Action'
    }
    
    container.querySelector('.chain-target-icon').textContent = this.getActionIcon(targetNode?.action_type)
    container.querySelector('.chain-target-name').textContent = targetNode?.name || 'Action'
    
    // Enabled state
    container.querySelector('.chain-enabled-checkbox').checked = edge.enabled !== false
    
    this.showModal(template)
  }

  showModal(content) {
    this.modalContentTarget.innerHTML = ''
    this.modalContentTarget.appendChild(content)
    this.modalTarget.classList.remove('hidden')
  }

  closeModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.add('hidden')
      this.currentEdge = null
    }
  }

  async saveChain() {
    if (!this.currentEdge) return
    
    const checkbox = this.modalContentTarget.querySelector('.chain-enabled-checkbox')
    const enabled = checkbox?.checked ?? true
    
    // Update the edge in our local data
    this.currentEdge.enabled = enabled
    
    // Find and update the cable visually
    const cableKey = `edge-${this.currentEdge.id}`
    const cableData = this.cables.get(cableKey)
    if (cableData) {
      cableData.path.strokeColor = enabled ? this.colors.cable.normal : this.colors.cable.disabled
    }
    
    this.isDirty = true
    this.closeModal()
  }

  async deleteChain() {
    if (!this.currentEdge) return
    
    if (!confirm('Delete this chain connection?')) return
    
    // Remove from cables
    const cableKey = `edge-${this.currentEdge.id}`
    const cableData = this.cables.get(cableKey)
    if (cableData) {
      cableData.path.remove()
      cableData.grabHandle.remove()
      this.cables.delete(cableKey)
    }
    
    // Remove from graph data
    const edgeIndex = this.graphData.edges.findIndex(e => e.id === this.currentEdge.id)
    if (edgeIndex > -1) {
      this.graphData.edges.splice(edgeIndex, 1)
    }
    
    this.isDirty = true
    this.closeModal()
  }

  getActionBgClass(actionType) {
    const classes = {
      slack_message: 'bg-purple-100',
      http_forward: 'bg-blue-100',
      log: 'bg-amber-100',
      email: 'bg-green-100',
      agent: 'bg-indigo-100'
    }
    return classes[actionType] || 'bg-gray-100'
  }

  // === Utilities ===

  findParentGroup(item) {
    let current = item
    while (current) {
      if (current instanceof this.paperScope.Group && current.data?.type) {
        return current
      }
      current = current.parent
    }
    return null
  }

  snapToGrid(point) {
    const grid = this.gridSizeValue
    return new this.paperScope.Point(
      Math.round(point.x / grid) * grid,
      Math.round(point.y / grid) * grid
    )
  }

  truncateText(text, maxLength) {
    if (!text) return ''
    return text.length > maxLength ? text.slice(0, maxLength) + '...' : text
  }

  getTriggerIcon(type) {
    const icons = {
      webhook: '🔗',
      schedule: '⏰',
      slack_listener: '💬'
    }
    return icons[type] || '⚡'
  }

  getTriggerTypeLabel(type) {
    const labels = {
      webhook: 'Webhook',
      schedule: 'Schedule',
      slack_listener: 'Slack Event'
    }
    return labels[type] || 'Trigger'
  }

  getActionIcon(type) {
    const icons = {
      slack_message: '💬',
      http_forward: '🔗',
      log: '📝',
      email: '✉️',
      agent: '🤖'
    }
    return icons[type] || '⚡'
  }

  getActionTypeLabel(type) {
    const labels = {
      slack_message: 'Slack Message',
      http_forward: 'HTTP Forward',
      log: 'Log',
      email: 'Email',
      agent: 'AI Agent'
    }
    return labels[type] || 'Action'
  }

  getOutletIcon(type) {
    const icons = {
      button: '🔘',
      select: '📋',
      checkbox: '☑️',
      radio: '⭕'
    }
    return icons[type] || '🔘'
  }
}
